import { useIonRouter } from '@ionic/react';
import { type ComponentType, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useWorkspaceBootstrapStatus } from '@/core/hooks/use-workspace-bootstrap';
import { useWorkspaces } from '@/core/hooks/use-workspaces';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

interface WithProtectedWorkspaceRouteOptions {
  redirectTo?: string;
  allowedRoutes?: string[];
}

export function withProtectedWorkspaceRoute<P extends object>(
  Component: ComponentType<P>,
  options: WithProtectedWorkspaceRouteOptions = {},
) {
  const { redirectTo, allowedRoutes } = options;

  const defaultAllowedRoutes = [
    '/workspace/invitation/accept',
    '/workspace/invitation/reject',
    '/onboarding',
    // In-shell create must finish plan without being yanked to /onboarding.
    shellPaths.moreWorkspaceCreate,
  ];

  const routesToAllow = allowedRoutes ?? defaultAllowedRoutes;

  return function ProtectedWorkspaceComponent(props: P) {
    const router = useIonRouter();
    const currentWorkspace = useCurrentWorkspace();
    const { error } = useWorkspaces();
    const status = useWorkspaceBootstrapStatus();
    const location = useLocation();
    const pathname = location.pathname;

    const isAllowedRoute = routesToAllow.some((route) =>
      pathname?.includes(route),
    );

    const canRedirectToOnboarding =
      status === 'needs-onboarding' && !error && !isAllowedRoute;

    useEffect(() => {
      if (error && !currentWorkspace && !isAllowedRoute) {
        const from = encodeURIComponent(pathname);
        router.push(`/error?code=memberships&from=${from}`, 'root', 'replace');
        return;
      }

      if (canRedirectToOnboarding) {
        const target = redirectTo ?? '/onboarding';
        router.push(target, 'root', 'replace');
      }
    }, [
      canRedirectToOnboarding,
      currentWorkspace,
      error,
      isAllowedRoute,
      pathname,
      router,
    ]);

    // Always render the real component; redirects run in useEffect above.
    // Swapping to an empty IonPage corrupts Ionic StackManager DOM state.
    return <Component {...props} />;
  };
}
